import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Swal from 'sweetalert2';
import { TipoZonaService } from '../../../../../shared/services/tipo-zona.service';
import { NivelRiesgoService } from '../../../../../shared/services/nivel-riesgo.service';

import {
  PesvViasZonasService,
  PesvViasZonas,
  PesvViasZonasPayload,
} from '../../../../../shared/services/pesv-vias-zonas.service';
import { PesvViasInternasService } from '../../../../../shared/services/pesv-vias-internas.service';
import { SentidoCirculacionService } from '../../../../../shared/services/sentido-circulacion.service';
import { Utils } from '../../../../../shared/utils/Utils';

@Component({
  selector: 'app-vias-zonas',
  templateUrl: './vias-zonas.component.html',
  styleUrls: ['./vias-zonas.component.scss'],
})
export class ViasZonasComponent implements OnInit {
  form!: FormGroup;
  guardando = false;
  idZona: number | null = null;
  activeTab = 1;
  cargandoListado = false;
  editando = false;
  zonas: PesvViasZonas[] = [];
  zonasFiltradas: PesvViasZonas[] = [];
  filtro = '';
  page = 1;
  pageSize = 10;
  readonly Number = Number;
  mapUrl: SafeResourceUrl | null = null;

  // Catálogos
  viasTodas: any[] = [];   // para mostrar el nombre en el listado
  vias: any[] = [];        // solo activas, para el selector
  sentidosCirculacion: any[] = [];
  tiposZona: any[] = [];

  // TODO: reemplazar por el catálogo real (servicio) cuando lo tengas.
  // Los ids deben coincidir con los de tu tabla.
  nivelesRiesgo: any[] = [];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private sanitizer: DomSanitizer,
    private zonasService: PesvViasZonasService,
    private viasService: PesvViasInternasService,
    private sentidoCirculacionService: SentidoCirculacionService,
    private tipoZonaService: TipoZonaService,
    private nivelRiesgoService: NivelRiesgoService
  ) {}

  ngOnInit(): void {
    this.inicializarForm();
    this.getVias();
    this.getSentidosCirculacion();
    this.getTiposZona();
    this.cargarZonas();
    this.getNivelesRiesgo();
  }

  // ===== Catálogos =====

  // Obtener vías internas activas
  getVias(): void {
    this.viasService.getActiveVias().subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.viasTodas = (Array.isArray(lista) ? lista : []).map((via: any) => ({
          ...via,
          nombreConCodigo: `${via.codigo ?? ''} - ${via.nombre ?? ''}`.trim(),
        }));
        this.vias = this.viasTodas;
      },
      error: () => this.toastError('No se pudieron cargar las vías internas.'),
    });
  }

  // Obtener sentidos de circulación
  getSentidosCirculacion(): void {
    this.sentidoCirculacionService.getAll().subscribe({
      next: (sentidos) => (this.sentidosCirculacion = sentidos),
      error: () => this.toastError('No se pudieron cargar los sentidos de circulación.'),
    });
  }

  // Obtener tipos de zona
  getTiposZona(): void {
    this.tipoZonaService.getAll().subscribe({
      next: (tipos) => (this.tiposZona = tipos),
      error: () => this.toastError('No se pudieron cargar los tipos de zona.'),
    });
  }

  // Obtener niveles de riesgo
  getNivelesRiesgo(): void {
    this.nivelRiesgoService.getAll().subscribe({
      next: (niveles) => (this.nivelesRiesgo = niveles),
      error: () => this.toastError('No se pudieron cargar los niveles de riesgo.'),
    });
  }

  // Helpers para mostrar descripciones en el listado
  nombreVia(id: any): string {
    const via = this.viasTodas.find((item) => String(item.id) === String(id));
    return via?.nombreConCodigo ?? '';
  }

  // Nivel de riesgo: 1 = Bajo, 2 = Medio, 3 = Alto
  claseRiesgo(id: any): string {
    switch (Number(id)) {
      case 1: return 'bg-success';
      case 2: return 'bg-warning text-dark';
      case 3: return 'bg-danger';
      default: return 'bg-secondary';
    }
  }

  // ===== Listado =====

  cargarZonas(): void {
    if (this.cargandoListado) return;

    this.cargandoListado = true;
    this.zonasService.getAll().subscribe({
      next: (respuesta: any) => {
        const lista = Array.isArray(respuesta) ? respuesta : respuesta?.data;
        this.zonas = Array.isArray(lista) ? lista : [];
        this.aplicarFiltro();
        this.cargandoListado = false;
      },
      error: () => {
        this.zonas = [];
        this.zonasFiltradas = [];
        this.cargandoListado = false;
        this.toastError('No se pudieron cargar las zonas registradas.');
      },
    });
  }

  aplicarFiltro(): void {
    const termino = this.filtro.trim().toLowerCase();
    this.zonasFiltradas = this.zonas.filter((zona) => {
      if (!termino) return true;
      return [
        zona.codigo,
        zona.nombre,
        this.nombreVia(zona.via_id),
      ].some((valor) => String(valor ?? '').toLowerCase().includes(termino));
    });
    this.page = 1;
  }

  mostrarListado(): void {
    if (this.activeTab === 2) return;
    this.activeTab = 2;
  }

  // ===== Edición =====

  editarZona(zona: PesvViasZonas): void {
    if (!zona?.id) return;
    this.zonasService.getById(zona.id).subscribe({
      next: (respuesta: any) => {
        const datos = respuesta?.data ?? respuesta;
        this.idZona = Number(datos.id ?? zona.id);
        this.editando = true;
        this.form.patchValue({
          via_id: this.idCatalogo(datos.via_id ?? datos.via),
          codigo: datos.codigo,
          nombre: datos.nombre,
          tipo_zona: this.idCatalogo(datos.tipo_zona),
          descripcion: datos.descripcion,
          longitud_m: datos.longitud_m,
          ancho_m: datos.ancho_m,
          sentido_circulacion: this.idCatalogo(datos.sentido_circulacion),
          velocidad_maxima_kmh: datos.velocidad_maxima_kmh,
          nivel_riesgo: this.idCatalogo(datos.nivel_riesgo),
          latitud: datos.latitud,
          longitud: datos.longitud,
          estado: datos.estado ?? 1,
        });
        this.actualizarMapa();
        this.activeTab = 1;
      },
      error: () => this.toastError('No se pudo cargar la zona para editar.'),
    });
  }

  nuevaZona(): void {
    this.idZona = null;
    this.editando = false;
    this.form.reset({ estado: 1 });
    this.actualizarMapa();
    this.activeTab = 1;
  }

  cambiarEstado(zona: PesvViasZonas): void {
    if (!zona.id) return;
    const activar = Number(zona.estado) !== 1;

    Swal.fire({
      icon: 'warning',
      title: `${activar ? 'Activar' : 'Inactivar'} zona`,
      text: `¿Está seguro de ${activar ? 'activar' : 'inactivar'} esta zona?`,
      showCancelButton: true,
      confirmButtonText: `Sí, ${activar ? 'activar' : 'inactivar'}`,
      cancelButtonText: 'Cancelar',
      reverseButtons: true,
    }).then((resultado) => {
      if (!resultado.isConfirmed) return;

      const accion$ = activar
        ? this.zonasService.activar(zona.id as number)
        : this.zonasService.inactivar(zona.id as number);
      accion$.subscribe({
        next: () => {
          Swal.fire({
            icon: 'success',
            title: activar ? 'Zona activada' : 'Zona inactivada',
            text: `La zona fue ${activar ? 'activada' : 'inactivada'} correctamente.`,
            timer: 1600,
            showConfirmButton: false,
          });
          this.cargarZonas();
        },
        error: () => this.toastError(`No se pudo ${activar ? 'activar' : 'inactivar'} la zona.`),
      });
    });
  }

  // Navegación a los riesgos e inspecciones registrados para esta zona
  verRiesgos(zona: PesvViasZonas): void {
    if (!zona.id) return;
    this.router.navigate(['listado/vias-zonas/riesgos', zona.id]);
  }

  verInspecciones(zona: PesvViasZonas): void {
    if (!zona.id) return;
    this.router.navigate(['listado/vias-zonas/inspecciones', zona.id]);
  }

  // Devuelve el id como number (o null) aunque el backend lo envíe como
  // string ("3") o como objeto anidado ({ id: 3, ... }).
  // ng-select con bindValue compara con === contra el id del catálogo,
  // por eso el tipo debe coincidir exactamente.
  // OJO: no usar [compareWith] junto con bindValue: en ese caso ng-select
  // compara el objeto completo de la opción contra el id y nunca coincide.
  private idCatalogo(valor: any): number | null {
    const id = valor != null && typeof valor === 'object'
      ? (valor.id ?? valor.codigo)
      : valor;
    if (id === null || id === undefined || id === '') return null;
    const numero = Number(id);
    return Number.isFinite(numero) ? numero : null;
  }

  // ===== Mapa =====

  actualizarMapa(): void {
    const rawLat = this.form?.get('latitud')?.value;
    const rawLng = this.form?.get('longitud')?.value;

    if (rawLat === null || rawLat === '' || rawLng === null || rawLng === '') {
      this.mapUrl = null;
      return;
    }

    const latitud = Number(rawLat);
    const longitud = Number(rawLng);

    if (!Number.isFinite(latitud) || !Number.isFinite(longitud) ||
      latitud < -90 || latitud > 90 || longitud < -180 || longitud > 180) {
      this.mapUrl = null;
      return;
    }

    const delta = 0.01;
    const bbox = `${longitud - delta},${latitud - delta},${longitud + delta},${latitud + delta}`;
    const url = `https://www.openstreetmap.org/export/embed.html?bbox=${bbox}&layer=mapnik&marker=${latitud},${longitud}`;
    this.mapUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  // ===== Formulario =====

  inicializarForm(): void {
    this.form = this.fb.group({
      via_id: [null, Validators.required],
      codigo: [null, Validators.required],
      nombre: [null, Validators.required],
      tipo_zona: [null, Validators.required],
      descripcion: [null],

      longitud_m: [null, Validators.min(0)],
      ancho_m: [null, Validators.min(0)],
      sentido_circulacion: [null],
      velocidad_maxima_kmh: [null, Validators.min(0)],
      nivel_riesgo: [null],

      latitud: [null],
      longitud: [null],

      estado: [1],
    });
  }

  // ===== Guardado =====

  guardar(continuar = false): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      const camposInvalidos = Object.keys(this.form.controls)
        .filter((campo) => this.form.get(campo)?.invalid)
        .join(', ');
      Swal.fire({
        icon: 'warning',
        title: 'Campos incompletos',
        text: `Revisa los siguientes campos: ${camposInvalidos}.`,
      });
      return;
    }

    const payload: PesvViasZonasPayload = this.form.getRawValue();
    this.guardando = true;

    const peticion$ = this.idZona
      ? this.zonasService.update(this.idZona, payload)
      : this.zonasService.create(payload);

    peticion$.subscribe({
      next: () => this.finalizarGuardado(continuar),
      error: (err) => {
        this.guardando = false;
        this.toastError(err?.error?.message ?? err?.error?.error ?? 'Ocurrió un error al guardar la zona.');
      },
    });
  }

  private finalizarGuardado(continuar: boolean): void {
    this.guardando = false;
    Swal.fire({
      icon: 'success',
      title: this.idZona ? 'Zona actualizada' : 'Zona registrada',
      text: 'La información se guardó correctamente.',
      timer: 1800,
      showConfirmButton: false,
    });

    if (continuar && !this.idZona) {
      const viaId = this.form.get('via_id')?.value;
      this.form.reset({ via_id: viaId, estado: 1 });
      this.actualizarMapa();
    } else {
      this.cargarZonas();
      this.editando = false;
      this.idZona = null;
      this.form.reset({ estado: 1 });
      this.actualizarMapa();
      this.activeTab = 2;
    }
  }

  guardarYContinuar(): void {
    this.guardar(true);
  }

  cancelar(): void {
    this.nuevaZona();
    this.activeTab = 2;
    this.cargarZonas();
  }

  private toastError(mensaje: string): void {
    Swal.fire({ icon: 'error', title: 'Error', text: mensaje });
  }

  exportarExcel(): void {
    Utils.exportVisibleTableToExcel(
      'tblViasZonas',
      'listado_vias_zonas',
      'Zonas de vías',
    );
  }

  exportarPdf(): void {
    Utils.exportToPDF(
      'tblViasZonas',
      'listado_vias_zonas',
      'Listado de zonas de vías',
      true,
    );
  }
}